import { DropdownField } from 'src/modules/common/components/DropdownField/DropdownField.tsx';
import { authorityValues } from 'src/modules/user-input/calcMaxDP/authorityValues.ts';
import { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { reducer } from 'src/modules/user-input/calcMaxDP/reducer.ts';
import { initialState } from 'src/modules/user-input/calcMaxDP/initialState.ts';
import { DecimalField } from 'src/modules/common/components/DecimalField/DecimalField.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { UnitSelector } from 'src/modules/user-input/components/UnitSelector.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { targetUnits } from 'src/modules/user-input/targetUnits.ts';
import { calcMaxDp } from 'src/modules/user-input/calcMaxDP/calcMaxDp.ts';
import { convert } from 'src/modules/user-input/convert.ts';
import { CalculatorModalLayout } from 'src/modules/common/layouts/CalculatorModalLayout.tsx';
import {
    CalculatorModalMainSection,
} from 'src/modules/common/components/CalculatorModalMainSection/CalculatorModalMainSection.tsx';
import { Units } from 'uom-units';
import { numberFormatter } from 'src/utils/numberFormatter.ts';
import { CalcKindEnum } from 'shared/enums/CalcKindEnum';
import { Modal } from 'src/modules/common/components/Modal/Modal.tsx';
import { ModalHeader } from 'src/modules/common/components/Modal/ModalHeader.tsx';
import { ModalBody } from 'src/modules/common/components/Modal/ModalBody.tsx';
import { ModalFooter } from 'src/modules/common/components/Modal/ModalFooter.tsx';
import { Button } from 'src/modules/common/components/Button/Button';
import { ModalProps } from 'src/modules/user-input/types';
import { ConfigContext } from 'src/configContext';

type Props = ModalProps & {
    kind: CalcKindEnum;
}

export const CalcMaxDP = ({
    opened,
    title,
    titleResult,
    description,
    allowedUnits,
    targetUnit,
    kind,
    handleModalTransfer,
    handleModalClose,
}: Props) => {
    const { userSettings: { currentLocale } } = useContext(ConfigContext);

    const [value, setValue] = useState('');
    const [unit, setUnit] = useState(targetUnit);

    const [state, dispatch] = useReducer(
        reducer,
        {
            ...initialState,
            dpmvUnit: kind === CalcKindEnum.Steam ? Units.Bar : targetUnits.dpmv,
        },
    );
    const dpv100 = useMemo(() => convert(
        calcMaxDp(
            convert(state.dpmv, state.dpmvUnit, targetUnits.dpmv),
            state.pv,
        ),
        Units.KiloPascal,
        unit,
    ), [state, unit]);

    useEffect(() => {
        setValue(dpv100);
    }, [dpv100]);

    useEffect(() => {
        setUnit(targetUnit);
    }, [targetUnit]);

    return (
        <Modal
            opened={opened}
            handleClose={handleModalClose}
        >
            <ModalHeader
                handleClose={handleModalClose}
                handleReset={() => dispatch({ type: 'reset' })}
                title={title}
            >
                <I18n messageId={description}/>
            </ModalHeader>

            <ModalBody>
                <CalculatorModalLayout>
                    <CalculatorModalMainSection
                        title={titleResult}
                        result={numberFormatter(dpv100, currentLocale)}
                        selectedUnit={unit}
                        onUnitChange={setUnit}
                        allowedUnits={allowedUnits.inletOutlet}
                    >
                        <DropdownField
                            options={authorityValues}
                            selectedValue={state.pv}
                            onChange={(payload) => {
                                dispatch({ type: 'pv_change', payload });
                            }}
                        >
                            <I18n messageId={MESSAGES.pv}/>
                        </DropdownField>
                        <DecimalField
                            name="dpmv"
                            inputSize="md"
                            labelText={MESSAGES.DPMV}
                            value={state.dpmv}
                            endAdornment={(
                                <UnitSelector
                                    selected={state.dpmvUnit.name}
                                    values={kind === CalcKindEnum.Steam ? [allowedUnits.dpmv[1]] : allowedUnits.dpmv}
                                    onChange={(payload) => {
                                        dispatch({ type: 'dpmv_unit', payload });
                                    }}
                                />
                            )}
                            currentLocale={currentLocale}
                            onValueChange={(payload) => {
                                dispatch({
                                    type: 'dpmv_change',
                                    payload,
                                });
                            }}
                            autoComplete="off"
                        />
                    </CalculatorModalMainSection>
                </CalculatorModalLayout>
            </ModalBody>

            <ModalFooter>
                <Button
                    kind="secondary"
                    fullWidth={true}
                    onClick={handleModalClose}
                >
                    <I18n messageId={MESSAGES.CANCEL}/>
                </Button>

                <Button
                    fullWidth={true}
                    onClick={() => {
                        handleModalTransfer({
                            value,
                            unit,
                        });
                    }}
                >
                    <I18n messageId={MESSAGES.apply}/>
                </Button>
            </ModalFooter>
        </Modal>
    );
};
